import { FC, ReactNode, useState } from "react";
import {
  OakFlex,
  OakIcon,
  OakBox,
  OakSpan,
  OakLink,
} from "@oaknational/oak-components";

export interface MiniDropdownProps {
  label: string;
  isToggleOpen?: boolean; // Optional for uncontrolled mode
  defaultOpen?: boolean; // Default state for uncontrolled mode
  isHovered?: boolean;
  children: ReactNode;
  onToggle?: (isOpen: boolean) => void;
  id?: string;
}

const MiniDropdown: FC<MiniDropdownProps> = ({
  label,
  isToggleOpen,
  defaultOpen = false,
  isHovered = false,
  children,
  onToggle,
  id,
}) => {
  // Use internal state if not controlled
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  // Determine if component is controlled or uncontrolled
  const isControlled = isToggleOpen !== undefined;
  const isOpen = isControlled ? isToggleOpen : internalIsOpen;

  const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    const newIsOpen = !isOpen;

    // Update internal state if uncontrolled
    if (!isControlled) {
      setInternalIsOpen(newIsOpen);
    }

    // Call onToggle callback with new state
    onToggle?.(newIsOpen);
  };

  const dropdownId =
    id || `mini-dropdown-${label.toLowerCase().replaceAll(/\s+/g, "-")}`;
  const contentId = `${dropdownId}-content`;

  return (
    <OakFlex $width="fit-content" $flexDirection={"column"}>
      <OakBox $width="fit-content">
        <OakLink
          element="button"
          onClick={handleToggle}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggle(e);
            }
          }}
          aria-expanded={isOpen}
          aria-controls={contentId}
          aria-label={`${label} dropdown, ${isOpen ? "expanded" : "collapsed"}`}
          style={{ textDecoration: "none" }}
        >
          <OakFlex
            $flexDirection={"row"}
            $alignSelf="flex-start"
            $alignItems={"center"}
            $flexShrink={1}
          >
            <OakSpan
              $font="heading-7"
              $color={isOpen ? "navy120" : "navy"}
              $textDecoration={isHovered && !isOpen ? "underline" : "none"}
              $textAlign={"center"}
            >
              {label}
            </OakSpan>
            <OakIcon
              iconName={isOpen ? "chevron-up" : "chevron-down"}
              $colorFilter={isOpen ? "navy120" : "navy"}
              $width="spacing-24"
              aria-hidden="true"
            />
          </OakFlex>
        </OakLink>
      </OakBox>
      <OakBox
        $transition="standard-ease"
        id={contentId}
        aria-labelledby={dropdownId}
        $display={isOpen ? "block" : "none"}
      >
        {children}
      </OakBox>
    </OakFlex>
  );
};

export default MiniDropdown;
